import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import type { ticketDetails } from 'shared';
import type { flightEntity } from 'freedom-shared';

import { groupDirectFlights, getCurrentFlightTimeAndDate } from '../lib';
import type { DirectFlightGroup, StoredTimes, Airline } from '../types';

export const useDirectFlightsStore = defineStore('directFlightsStore', () => {
  const directOffers = ref<flightEntity.types.MappedOffer[]>([]);
  const isRoundTrip = ref(false);
  const selectedAirline = ref<Airline | null>(null);
  const airlineSelectedTimes = ref<Record<string, StoredTimes>>({});
  const isOfferFromDirectFlights = ref(false);

  const groups = computed<DirectFlightGroup[]>(() =>
    groupDirectFlights(directOffers.value, isRoundTrip.value),
  );

  const hasGroups = computed(() => groups.value.length > 0);

  const airlines = computed(() => {
    const airlineMap = new Map<string, Airline>();
    groups.value.forEach(group => {
      if (!airlineMap.has(group.airline.iata)) {
        airlineMap.set(group.airline.iata, group.airline);
      }
    });
    return Array.from(airlineMap.values());
  });

  const selectedGroup = computed(() => {
    if (!selectedAirline.value) {
      return groups.value[0] || null;
    }
    return (
      groups.value.find(g => g.airline.iata === selectedAirline.value?.iata) ||
      null
    );
  });

  const filteredOffersByAirline = computed(() => {
    return selectedGroup.value?.offers || [];
  });

  // Reset selectedAirline if it's no longer in groups
  watch(groups, newGroups => {
    if (
      selectedAirline.value &&
      !newGroups.some(g => g.airline.iata === selectedAirline.value?.iata)
    ) {
      selectedAirline.value = newGroups[0]?.airline || null;
    }
  });

  // Actions
  function setOffers(newOffers: flightEntity.types.MappedOffer[]) {
    directOffers.value = newOffers;
  }

  function setIsRoundTrip(value: boolean) {
    isRoundTrip.value = value;
  }

  function selectAirline(airline: Airline | null) {
    selectedAirline.value = airline;
  }

  function setIsOfferFromDirectFlights(value: boolean) {
    isOfferFromDirectFlights.value = value;
  }

  function saveTimesForAirline(
    airlineIata: string,
    offer: flightEntity.types.MappedOffer | ticketDetails.types.MappedOffer,
  ) {
    const { time: forwardTime, date: forwardDate } =
      getCurrentFlightTimeAndDate(offer, 0);

    if (isRoundTrip.value) {
      const { time: backwardTime, date: backwardDate } =
        getCurrentFlightTimeAndDate(offer, 1);

      airlineSelectedTimes.value[airlineIata] = {
        forwardTime,
        forwardDate,
        backwardTime,
        backwardDate,
      };
    } else {
      airlineSelectedTimes.value[airlineIata] = {
        time: forwardTime,
        date: forwardDate,
      };
    }
  }

  function getStoredTimesForAirline(
    airlineIata: string,
  ): StoredTimes | undefined {
    return airlineSelectedTimes.value[airlineIata];
  }

  function reset() {
    directOffers.value = [];
    isRoundTrip.value = false;
    selectedAirline.value = null;
    airlineSelectedTimes.value = {};
  }

  return {
    // State
    directOffers,
    isRoundTrip,
    selectedAirline,
    airlineSelectedTimes,
    // Computed
    groups,
    hasGroups,
    airlines,
    selectedGroup,
    filteredOffersByAirline,
    isOfferFromDirectFlights,
    // Actions
    setOffers,
    setIsRoundTrip,
    selectAirline,
    setIsOfferFromDirectFlights,
    saveTimesForAirline,
    getStoredTimesForAirline,
    reset,
  };
});
